/**
 * ProtectedRoute.jsx
 * CCH Care — Routing
 * Last Modified: 2026-03-26
 *
 * Wraps routes that require authentication.
 * Redirects to login if not authenticated.
 * Optionally checks for specific permissions.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/useAuth';
import { COLORS } from '../../config/constants';

export default function ProtectedRoute({ children, permission }) {
  const { isAuthenticated, loading, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: COLORS.bg,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40, height: 40, border: `3px solid ${COLORS.border}`,
            borderTopColor: COLORS.primary, borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 16px',
          }} />
          <div style={{ color: COLORS.textMuted, fontSize: 14, fontWeight: 500 }}>
            Loading...
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Optional permission check
  if (permission && !hasPermission(permission)) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', background: COLORS.bg, padding: 40,
      }}>
        <div style={{ textAlign: 'center', maxWidth: 400 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 8 }}>
            Access Denied
          </div>
          <div style={{ fontSize: 15, color: COLORS.textMuted }}>
            You don&apos;t have permission to access this page. Contact your administrator.
          </div>
        </div>
      </div>
    );
  }

  return children;
}
